// LeadImportExport.tsx - Bulk import/export interface
import React, { useState, useRef } from 'react';
import {
  Card,
  Text,
  Button,
  Stack,
  Group,
  FileInput,
  Switch,
  Alert,
  Progress,
  List,
  ThemeIcon,
  Tabs,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconUpload,
  IconDownload,
  IconFileTypeCsv,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';

interface ImportResult {
  success: boolean;
  imported: number;
  updated: number;
  skipped: number;
  errors: Array<{
    row: number;
    field: string;
    message: string;
  }>;
}

export function LeadImportExport() {
  const [file, setFile] = useState<File | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<any>(null);

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await fetch('/api/leads/import', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Import failed');
      }

      return response.json();
    },
    onSuccess: (data) => {
      setImportResult(data.data);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }

      notifications.show({
        title: 'Import Complete',
        message: `Successfully imported ${data.data.imported} leads`,
        color: 'green',
        icon: <IconCheck size={18} />,
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Import Failed',
        message: error instanceof Error ? error.message : 'Failed to import leads',
        color: 'red',
        icon: <IconX size={18} />,
      });
    },
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/leads/export', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    onSuccess: () => {
      notifications.show({
        title: 'Export Complete',
        message: 'Leads exported successfully',
        color: 'green',
        icon: <IconCheck size={18} />,
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Export Failed',
        message: error instanceof Error ? error.message : 'Failed to export leads',
        color: 'red',
        icon: <IconX size={18} />,
      });
    },
  });

  // Download template
  const downloadTemplate = async () => {
    try {
      const response = await fetch('/api/leads/import/template', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leads-import-template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      notifications.show({
        title: 'Template Downloaded',
        message: 'Import template downloaded successfully',
        color: 'blue',
      });
    } catch (error) {
      notifications.show({
        title: 'Download Failed',
        message: 'Failed to download template',
        color: 'red',
      });
    }
  };

  // Handle import
  const handleImport = () => {
    if (!file) {
      notifications.show({
        title: 'No File Selected',
        message: 'Please select a CSV file to import',
        color: 'orange',
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('skipDuplicates', skipDuplicates.toString());

    importMutation.mutate(formData);
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Tabs defaultValue="import">
        <Tabs.List>
          <Tabs.Tab value="import" leftSection={<IconUpload size={16} />}>
            Import Leads
          </Tabs.Tab>
          <Tabs.Tab value="export" leftSection={<IconDownload size={16} />}>
            Export Leads
          </Tabs.Tab>
        </Tabs.List>

        {/* Import Tab */}
        <Tabs.Panel value="import" pt="md">
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Import leads from a CSV file. Download the template to see the required format.
            </Text>

            {/* Template Download */}
            <Alert icon={<IconInfoCircle size={16} />} color="blue">
              <Group justify="space-between">
                <Text size="sm">Need help getting started?</Text>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconDownload size={14} />}
                  onClick={downloadTemplate}
                >
                  Download Template
                </Button>
              </Group>
            </Alert>

            {/* File Input */}
            <FileInput
              ref={fileInputRef}
              label="Select CSV File"
              placeholder="Choose a file to import"
              accept=".csv"
              leftSection={<IconFileTypeCsv size={16} />}
              value={file}
              onChange={setFile}
              clearable
            />

            {/* Options */}
            <Switch
              label="Skip duplicate leads"
              description="Ignore leads that already exist in the system"
              checked={skipDuplicates}
              onChange={(e) => setSkipDuplicates(e.currentTarget.checked)}
            />

            {/* Import Button */}
            <Button
              leftSection={<IconUpload size={18} />}
              onClick={handleImport}
              loading={importMutation.isPending}
              disabled={!file}
              fullWidth
            >
              Import Leads
            </Button>

            {/* Import Progress */}
            {importMutation.isPending && (
              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Importing leads...
                  </Text>
                  <Progress value={50} animated />
                </Stack>
              </Card>
            )}

            {/* Import Results */}
            {importResult && (
              <Card withBorder padding="md" bg={importResult.success ? 'green.0' : 'yellow.0'}>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text size="lg" fw={700}>
                      Import Results
                    </Text>
                    <Badge color={importResult.success ? 'green' : 'yellow'} size="lg">
                      {importResult.success ? 'Success' : 'Completed with errors'}
                    </Badge>
                  </Group>

                  <Group grow>
                    <Card withBorder padding="sm">
                      <Text size="xs" c="dimmed">
                        Imported
                      </Text>
                      <Text size="xl" fw={700} c="green">
                        {importResult.imported}
                      </Text>
                    </Card>
                    <Card withBorder padding="sm">
                      <Text size="xs" c="dimmed">
                        Updated
                      </Text>
                      <Text size="xl" fw={700} c="blue">
                        {importResult.updated}
                      </Text>
                    </Card>
                    <Card withBorder padding="sm">
                      <Text size="xs" c="dimmed">
                        Skipped
                      </Text>
                      <Text size="xl" fw={700} c="orange">
                        {importResult.skipped}
                      </Text>
                    </Card>
                  </Group>

                  {/* Errors */}
                  {importResult.errors.length > 0 && (
                    <>
                      <Alert icon={<IconAlertCircle size={16} />} color="red">
                        <Text size="sm" fw={500} mb="xs">
                          {importResult.errors.length} error(s) found:
                        </Text>
                      </Alert>
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Row</Table.Th>
                            <Table.Th>Field</Table.Th>
                            <Table.Th>Error</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {importResult.errors.slice(0, 10).map((error, idx) => (
                            <Table.Tr key={idx}>
                              <Table.Td>{error.row}</Table.Td>
                              <Table.Td>
                                <Badge size="sm">{error.field}</Badge>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">{error.message}</Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                      {importResult.errors.length > 10 && (
                        <Text size="sm" c="dimmed" ta="center">
                          ... and {importResult.errors.length - 10} more errors
                        </Text>
                      )}
                    </>
                  )}
                </Stack>
              </Card>
            )}

            {/* Format Guide */}
            <Card withBorder padding="md" bg="gray.0">
              <Text size="sm" fw={500} mb="xs">
                CSV Format Requirements:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>
                  <strong>Required fields:</strong> customerName, email, carInterest
                </List.Item>
                <List.Item>
                  <strong>Optional fields:</strong> phone, budget, timeline, source, message
                </List.Item>
                <List.Item>Use UTF-8 encoding</List.Item>
                <List.Item>First row must contain column headers</List.Item>
                <List.Item>Maximum file size: 10MB</List.Item>
              </List>
            </Card>
          </Stack>
        </Tabs.Panel>

        {/* Export Tab */}
        <Tabs.Panel value="export" pt="md">
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Export all your leads to a CSV file for backup or external use.
            </Text>

            <Alert icon={<IconInfoCircle size={16} />} color="blue">
              The exported file will include all lead information including custom fields and
              metadata.
            </Alert>

            <Card withBorder padding="md" bg="gray.0">
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Export includes:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item icon={<ThemeIcon color="green" size={20} radius="xl"><IconCheck size={12} /></ThemeIcon>}>
                    All lead contact information
                  </List.Item>
                  <List.Item icon={<ThemeIcon color="green" size={20} radius="xl"><IconCheck size={12} /></ThemeIcon>}>
                    Lead status and timeline
                  </List.Item>
                  <List.Item icon={<ThemeIcon color="green" size={20} radius="xl"><IconCheck size={12} /></ThemeIcon>}>
                    Vehicle interests and budget
                  </List.Item>
                  <List.Item icon={<ThemeIcon color="green" size={20} radius="xl"><IconCheck size={12} /></ThemeIcon>}>
                    Lead source and scores
                  </List.Item>
                  <List.Item icon={<ThemeIcon color="green" size={20} radius="xl"><IconCheck size={12} /></ThemeIcon>}>
                    Created and updated timestamps
                  </List.Item>
                </List>
              </Stack>
            </Card>

            <Button
              leftSection={<IconDownload size={18} />}
              onClick={() => exportMutation.mutate()}
              loading={exportMutation.isPending}
              size="lg"
              fullWidth
            >
              Export All Leads to CSV
            </Button>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Card>
  );
}
